import axios from '~/utils/axios';

// statuses of a partition, from the end of its life (archived) to its start (live)
export const PARTITION_STATUSES = [
  'DROPPED',
  'UPLOADED',
  'EXPORTING',
  'PENDING',
  'FAILED',
  'RETAINED_NO_STORAGE',
  'EXPIRED',
  'LIVE',
] as const;

export type PartitionStatus = typeof PARTITION_STATUSES[number];

export type ArchivingStatus = {
  status: 'ENABLED' | 'DISABLED' | 'MISCONFIGURED';
  reason?: string | null;
};

// partitions of a table in one status: [from, to) they span, rows / stored bytes where known
export type PartitionCoverage = {
  status: PartitionStatus;
  count: number;
  from: string;
  to: string;
  rowCount?: number | null;
  byteSize?: number | null;
};

// id - "<service>.<tableName>"; archiving - for ARCHIVE tables only
export type PartitionedTable = {
  id: string;
  tableName: string;
  retention: string;
  retentionPolicy: 'ARCHIVE' | 'DROP';
  archiving?: ArchivingStatus | null;
  coverage: PartitionCoverage[];
};

export type PartitionArchiveIssue = {
  type: 'SERVICE_UNAVAILABLE' | 'ARCHIVE_FAILED' | 'RETAINED_NO_STORAGE';
  service?: string | null;
  table?: string | null;
  count?: number | null;
  reason?: string | null;
};

export type PartitionArchiveOverview = {
  tables: PartitionedTable[];
  issues: PartitionArchiveIssue[];
};

// item of GET /partitionArchive/partitions (react-admin resource 'partitionArchive/partitions');
// the fields after `attached` come from the partition's archive queue row, absent without one
export type PartitionArchivePartition = {
  id: string;
  tableName: string;
  partitionName: string;
  partitionFrom: string;
  partitionTo: string;
  status: PartitionStatus;
  attached: boolean;
  frozenName?: string | null;
  exportAttempts?: number | null;
  nextClaimAt?: string | null;
  lastError?: string | null;
  objectLocation?: string | null;
  objectKey?: string | null;
  byteSize?: number | null;
  rowCount?: number | null;
  sha256?: string | null;
  enqueuedAt?: string | null;
  updatedAt?: string | null;
};

export const getPartitionArchiveOverview = () =>
  axios.get<PartitionArchiveOverview>('/partitionArchive/overview').then((res) => res.data);
